import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ImagePlus,
  Loader2,
  LogOut,
  Pencil,
  Trash2,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "zion_admin_token";

const imgSrc = (url) =>
  url && url.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const formatError = (e, fallback) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg).filter(Boolean).join(" ");
  return fallback;
};

const inputClass =
  "w-full rounded-sm border border-slate-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-slate-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15";

const FilePicker = ({ label, file, onChange, testid, required }) => (
  <div>
    <span className="mb-1.5 block text-sm font-medium text-ink">{label}</span>
    <label
      className={`flex cursor-pointer items-center justify-center gap-2 rounded-sm border border-dashed px-4 py-4 text-sm transition-colors ${
        file
          ? "border-brand bg-brand-light text-brand"
          : "border-slate-300 text-slate-500 hover:border-brand/50 hover:text-brand"
      }`}
    >
      <ImagePlus className="h-4 w-4" strokeWidth={1.8} />
      {file ? file.name : "Choisir une image (JPEG, PNG, WebP)"}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        required={required}
        data-testid={testid}
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
    </label>
  </div>
);

const EditRow = ({ item, onCancel, onSaved, authHeaders }) => {
  const [title, setTitle] = useState(item.title || "");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      if (beforeFile) fd.append("before_image", beforeFile);
      if (afterFile) fd.append("after_image", afterFile);
      await axios.put(`${API}/admin/gallery/${item.id}`, fd, { headers: authHeaders });
      toast.success("Paire mise à jour.");
      onSaved();
    } catch (err) {
      toast.error(formatError(err, "Erreur lors de la mise à jour."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      data-testid={`gallery-edit-form-${item.id}`}
      className="mt-4 space-y-4 border-t border-slate-200 pt-4"
    >
      <div>
        <span className="mb-1.5 block text-sm font-medium text-ink">Titre (optionnel)</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid={`gallery-edit-title-${item.id}`}
          className={inputClass}
          placeholder="Ex. Cuisine — Carouge"
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FilePicker
          label="Remplacer la photo « Avant »"
          file={beforeFile}
          onChange={setBeforeFile}
          testid={`gallery-edit-before-${item.id}`}
        />
        <FilePicker
          label="Remplacer la photo « Après »"
          file={afterFile}
          onChange={setAfterFile}
          testid={`gallery-edit-after-${item.id}`}
        />
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          data-testid={`gallery-edit-save-${item.id}`}
          className="inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-deep disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Enregistrer
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid={`gallery-edit-cancel-${item.id}`}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:border-slate-400"
        >
          <X className="h-4 w-4" />
          Annuler
        </button>
      </div>
    </form>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTitle, setNewTitle] = useState("");
  const [newBefore, setNewBefore] = useState(null);
  const [newAfter, setNewAfter] = useState(null);
  const [creating, setCreating] = useState(false);
  const addFormRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    setLoadingItems(true);
    try {
      const res = await axios.get(`${API}/gallery`);
      setItems(res.data.items || []);
    } catch {
      toast.error("Impossible de charger la galerie.");
    } finally {
      setLoadingItems(false);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setUser(res.data);
        fetchItems();
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setChecking(false));
  }, [token, fetchItems]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
      setToken(res.data.access_token);
      toast.success("Connexion réussie.");
    } catch (err) {
      setLoginError(formatError(err, "Identifiants incorrects."));
    } finally {
      setLoggingIn(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  const onCreate = async (e) => {
    e.preventDefault();
    if (!newBefore || !newAfter) {
      toast.error("Veuillez ajouter une photo « Avant » et une photo « Après ».");
      return;
    }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("title", newTitle);
      fd.append("before_image", newBefore);
      fd.append("after_image", newAfter);
      await axios.post(`${API}/admin/gallery`, fd, { headers: authHeaders });
      toast.success("Paire avant / après ajoutée.");
      setNewTitle("");
      setNewBefore(null);
      setNewAfter(null);
      addFormRef.current?.reset();
      fetchItems();
    } catch (err) {
      if (err?.response?.status === 401) return onLogout();
      toast.error(formatError(err, "Erreur lors de l’ajout."));
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (item) => {
    if (!window.confirm("Supprimer cette paire de photos ? Elle ne sera plus visible sur le site."))
      return;
    try {
      await axios.delete(`${API}/admin/gallery/${item.id}`, { headers: authHeaders });
      toast.success("Paire supprimée.");
      fetchItems();
    } catch (err) {
      if (err?.response?.status === 401) return onLogout();
      toast.error("Erreur lors de la suppression.");
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-mist">
        <Loader2 className="h-8 w-8 animate-spin text-brand" data-testid="admin-loading" />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen flex-col bg-mist">
        <div className="mx-auto flex w-full max-w-7xl items-center px-4 py-6 sm:px-8">
          <Link
            to="/"
            data-testid="admin-back-home-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-brand"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.8} />
            Retour au site
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center px-4 pb-20">
          <form
            onSubmit={onLogin}
            data-testid="admin-login-form"
            className="w-full max-w-md rounded-sm border border-slate-200 bg-white p-8 shadow-[0_18px_45px_rgba(15,23,42,0.06)] sm:p-10"
          >
            <p className="font-display text-[15px] font-semibold tracking-[0.08em] text-ink">
              ZION SOLUTIONS <span className="text-brand">SUISSE SÀRL</span>
            </p>
            <h1 className="mt-6 font-display text-2xl font-medium tracking-tight text-ink">
              Espace pro
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Accès réservé à l’administration de la galerie Avant / Après.
            </p>
            <div className="mt-8 space-y-5">
              <div>
                <label htmlFor="admin-email" className="mb-1.5 block text-sm font-medium text-ink">
                  Adresse e-mail
                </label>
                <input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email-input"
                  className={inputClass}
                  autoComplete="username"
                  required
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="mb-1.5 block text-sm font-medium text-ink">
                  Mot de passe
                </label>
                <input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className={inputClass}
                  autoComplete="current-password"
                  required
                />
              </div>
              {loginError && (
                <p data-testid="admin-login-error" className="text-sm font-medium text-red-600" role="alert">
                  {loginError}
                </p>
              )}
              <button
                type="submit"
                disabled={loggingIn}
                data-testid="admin-login-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-deep disabled:opacity-60"
              >
                {loggingIn && <Loader2 className="h-4 w-4 animate-spin" />}
                Se connecter
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-mist">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-8">
          <div>
            <p className="font-display text-base font-semibold tracking-[0.06em] text-ink">
              ZION SOLUTIONS <span className="text-brand">SUISSE SÀRL</span>
            </p>
            <p className="text-xs text-slate-500">Galerie Avant / Après — {user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-brand hover:text-brand"
            >
              Voir le site
            </Link>
            <button
              type="button"
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-red-300 hover:text-red-600"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.8} />
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <section className="rounded-sm border border-slate-200 bg-white p-6 sm:p-8">
          <h2 className="font-display text-xl font-medium tracking-tight text-ink">
            Ajouter une paire Avant / Après
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Les deux photos apparaîtront côte à côte dans la galerie du site.
          </p>
          <form ref={addFormRef} onSubmit={onCreate} data-testid="gallery-add-form" className="mt-6 space-y-4">
            <div>
              <span className="mb-1.5 block text-sm font-medium text-ink">Titre (optionnel)</span>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                data-testid="gallery-add-title"
                className={inputClass}
                placeholder="Ex. Salle de bains — Champel"
                maxLength={120}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FilePicker
                label="Photo « Avant »"
                file={newBefore}
                onChange={setNewBefore}
                testid="gallery-add-before"
              />
              <FilePicker
                label="Photo « Après »"
                file={newAfter}
                onChange={setNewAfter}
                testid="gallery-add-after"
              />
            </div>
            <button
              type="submit"
              disabled={creating}
              data-testid="gallery-add-submit"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-deep disabled:opacity-60"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" strokeWidth={1.8} />}
              Ajouter à la galerie
            </button>
          </form>
        </section>

        <section className="mt-10">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-display text-xl font-medium tracking-tight text-ink">
              Paires publiées
            </h2>
            <span data-testid="gallery-count" className="text-sm text-slate-500">
              {items.length} paire{items.length > 1 ? "s" : ""}
            </span>
          </div>

          {loadingItems ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-brand" />
            </div>
          ) : items.length === 0 ? (
            <p data-testid="gallery-empty" className="rounded-sm border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
              Aucune paire pour le moment. Ajoutez votre première transformation ci-dessus.
            </p>
          ) : (
            <div className="space-y-6">
              {items.map((item) => (
                <article
                  key={item.id}
                  data-testid={`gallery-admin-item-${item.id}`}
                  className="rounded-sm border border-slate-200 bg-white p-5 sm:p-6"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="grid w-full max-w-xl grid-cols-2 gap-3">
                      <figure>
                        <img
                          src={imgSrc(item.before_url)}
                          alt={`Avant — ${item.title || "projet"}`}
                          className="aspect-[4/3] w-full rounded-sm border border-slate-200 object-cover"
                          loading="lazy"
                        />
                        <figcaption className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          Avant
                        </figcaption>
                      </figure>
                      <figure>
                        <img
                          src={imgSrc(item.after_url)}
                          alt={`Après — ${item.title || "projet"}`}
                          className="aspect-[4/3] w-full rounded-sm border border-slate-200 object-cover"
                          loading="lazy"
                        />
                        <figcaption className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-brand">
                          Après
                        </figcaption>
                      </figure>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingId(editingId === item.id ? null : item.id)}
                        data-testid={`gallery-edit-toggle-${item.id}`}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-brand hover:text-brand"
                      >
                        <Pencil className="h-4 w-4" strokeWidth={1.8} />
                        Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(item)}
                        data-testid={`gallery-delete-${item.id}`}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:border-red-300 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                        Supprimer
                      </button>
                    </div>
                  </div>
                  {item.title && (
                    <p className="mt-3 text-sm font-medium text-slate-700">{item.title}</p>
                  )}
                  {editingId === item.id && (
                    <EditRow
                      item={item}
                      authHeaders={authHeaders}
                      onCancel={() => setEditingId(null)}
                      onSaved={() => {
                        setEditingId(null);
                        fetchItems();
                      }}
                    />
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
